import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  FolderKanban,
  Search,
  X,
  ArrowUpDown,
  ChevronRight,
  Download,
  Upload,
  Calendar,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { statusConfig, ragPriority } from '../lib/portfolioConstants';
import RagBadge from '../components/RagBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');
  const [sortBy, setSortBy] = useState('rag');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/executive/projects/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'projects.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('Export failed', e); toast.error('Export failed'); }
  };

  const importCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!globalThis.confirm(`This will replace ALL projects with data from "${file.name}". Continue?`)) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/executive/projects/import`, form);
      toast.success(`Imported ${res.data.imported} projects`);
      fetchData();
    } catch (e) { console.error('Import failed', e); toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [projRes, teamRes] = await Promise.all([
        axios.get(`${API}/executive/projects`),
        axios.get(`${API}/teams/`)
      ]);
      setProjects(projRes.data || []);
      setTeams(teamRes.data || []);
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const openProject = (project) => {
    setSelectedProject(project);
    setShowModal(true);
  };

  const filtered = useMemo(() => {
    let result = [...projects];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(q) || p.key?.toLowerCase().includes(q) ||
        p.program_name?.toLowerCase().includes(q) || p.team_name?.toLowerCase().includes(q) ||
        p.owner?.toLowerCase().includes(q) || p.current_status_text?.toLowerCase().includes(q) ||
        p.risk_summary?.toLowerCase().includes(q)
      );
    }
    if (selectedTeamFilter !== 'all') {
      result = result.filter(p => p.team_id === selectedTeamFilter);
    }
    if (sortBy === 'rag') result.sort((a, b) => (ragPriority[a.status] ?? 5) - (ragPriority[b.status] ?? 5));
    else if (sortBy === 'progress') result.sort((a, b) => a.progress_percent - b.progress_percent);
    else if (sortBy === 'name') result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [projects, searchQuery, selectedTeamFilter, sortBy]);

  if (isLoading) {
    return (
      <div className="p-8 space-y-4 animate-pulse" data-testid="projects-loading">
        <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
        {[1,2,3].map(i => <div key={i} className="h-48 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>)}
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#00C26D]/10 dark:bg-[#00C26D]/15 rounded-xl flex items-center justify-center">
            <FolderKanban className="w-6 h-6 text-[#00995A] dark:text-[#00D97E]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Projects</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">{filtered.length} of {projects.length} projects (Epics)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D] hover:text-[#00995A] transition-all">
            <Download className="w-3 h-3" /> Export CSV
          </button>
          <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all ${importing ? 'opacity-50 pointer-events-none' : ''} bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D] hover:text-[#00995A]`}>
            <Upload className="w-3 h-3" /> {importing ? 'Importing…' : 'Import CSV'}
            <input type="file" accept=".csv" className="hidden" onChange={importCSV} disabled={importing} />
          </label>
          {['rag', 'progress', 'name'].map(s => (
            <button key={s} onClick={() => setSortBy(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${sortBy === s ? 'bg-[#00C26D] text-[#1A1A1A]' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'}`}
              data-testid={`sort-${s}`}
            >
              <ArrowUpDown className="w-3 h-3 inline mr-1" />{s === 'rag' ? 'RAG Status' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search projects by name, key, program, team, owner, status, or risk..."
          className="input-glass w-full pl-10 pr-10" data-testid="search-projects"
        />
        {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
      </div>

      {/* Team Pills */}
      <div className="flex flex-wrap gap-2" data-testid="team-pills">
        <button onClick={() => setSelectedTeamFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${selectedTeamFilter === 'all' ? 'bg-[#00C26D] text-[#1A1A1A] shadow-md shadow-[#00C26D]/20' : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D]/50'}`}
        >All Teams</button>
        {teams.map(t => (
          <button key={t.id} onClick={() => setSelectedTeamFilter(selectedTeamFilter === t.id ? 'all' : t.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${selectedTeamFilter === t.id ? 'bg-[#00C26D] text-[#1A1A1A] shadow-md shadow-[#00C26D]/20' : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D]/50'}`}
          >{t.name}</button>
        ))}
      </div>

      {/* Project Cards - 4-Blocker Format */}
      <div className="space-y-3">
        {filtered.map(project => {
          const sc = statusConfig[project.status] || statusConfig.green;
          const StatusIcon = sc.icon;
          return (
            <button key={project.id} type="button"
              className={`w-full text-left glass-card rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-all border-l-4 ${sc.border}`}
              onClick={() => openProject(project)}
              data-testid={`project-card-${project.key}`}
            >
              <div className="px-6 py-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${sc.bgLight} ${sc.textColor} flex items-center gap-1`}>
                    <StatusIcon className="w-3 h-3" />{sc.label}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">{project.key}</span>
                  <h3 className="font-semibold text-slate-900 dark:text-white text-sm">{project.name}</h3>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                  <span className="hidden lg:block">{project.team_name}</span>
                  <span className="hidden lg:block text-slate-400">|</span>
                  <span className="hidden lg:block">{project.program_name}</span>
                  <span className="font-mono">{project.progress_percent}%</span>
                  <div className="w-16 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className={`h-full ${sc.color} rounded-full`} style={{ width: `${project.progress_percent}%` }} />
                  </div>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-100 dark:divide-slate-800">
                <div className="p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#00995A] dark:text-[#00D97E] mb-1">IT Product Manager</p>
                  <p className="text-xs text-slate-700 dark:text-slate-300 line-clamp-2">{project.project_manager || '—'}</p>
                </div>
                <div className="p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">RAG Status</p>
                  <RagBadge rag={project.rag} />
                </div>
                <div className="p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-1">Tier</p>
                  <p className="text-xs text-slate-700 dark:text-slate-300">{project.tier ? `Tier ${project.tier}` : '—'}</p>
                </div>
                <div className="p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-1">IT POV</p>
                  <p className="text-xs text-slate-700 dark:text-slate-300 line-clamp-4">{project.it_pov || '—'}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Project Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {selectedProject && (() => {
            const sc = statusConfig[selectedProject.status] || statusConfig.green;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 flex-wrap">
                    <span className={`px-3 py-1.5 text-sm font-bold rounded ${sc.bgLight} ${sc.textColor}`}>{sc.label}</span>
                    {selectedProject.tier && (
                      <span className="px-3 py-1 text-xs font-bold rounded border bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600">
                        T{selectedProject.tier}
                      </span>
                    )}
                    <span className="text-sm text-slate-400 font-mono">{selectedProject.key}</span>
                    <span className="text-base font-semibold text-slate-900 dark:text-white">{selectedProject.name}</span>
                  </DialogTitle>
                </DialogHeader>

                {/* Meta row — Team, Program, IT PM as text */}
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 dark:text-slate-400 mt-2">
                  {selectedProject.team_name && <span>Team: <span className="text-slate-700 dark:text-slate-300">{selectedProject.team_name}</span></span>}
                  {selectedProject.program_name && <span>Program: <span className="text-slate-700 dark:text-slate-300">{selectedProject.program_name}</span></span>}
                  <span>IT TPM: <span className="text-slate-700 dark:text-slate-300">{selectedProject.project_manager || '—'}</span></span>
                </div>

                {/* Date tiles */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="p-4 rounded-xl border border-[#00C26D]/30 dark:border-[#00C26D]/30 bg-[#00C26D]/5 dark:bg-[#00C26D]/10">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#00995A] dark:text-[#00D97E] mb-2 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />Start Date
                    </h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{selectedProject.planned_start || selectedProject.start_date || '—'}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-orange-700 dark:text-orange-400 mb-2 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />Go-Live Date
                    </h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{selectedProject.due_date || selectedProject.go_live_date || '—'}</p>
                  </div>
                </div>

                {/* Milestones/Risk (IT POV) */}
                <div className="mt-4 p-4 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 min-h-[80px]">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-purple-700 dark:text-purple-400 mb-2">Milestones/Risk</h4>
                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{selectedProject.it_pov || '—'}</p>
                </div>

                {/* Status */}
                {selectedProject.current_status_text && (
                  <div className="mt-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Status</h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{selectedProject.current_status_text}</p>
                  </div>
                )}

                {/* Contributing Teams — bottom */}
                {selectedProject.contributing_team && (
                  <div className="mt-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />Contributing Teams
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.contributing_team.replaceAll(';', ',').split(',').map(t => t.trim()).filter(Boolean).map(team => (
                        <span key={team} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00C26D]/10 dark:bg-[#00C26D]/15 text-[#00995A] dark:text-[#00D97E] border border-[#00C26D]/30 dark:border-[#00C26D]/30">
                          {team}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsPage;
